import React, { forwardRef, useRef, useState } from 'react';
import { Form, Typography } from 'antd';
import { FieldTypes } from 'utils/formUtils';
import _, { isEmpty, uniq } from 'lodash';
import moment from 'moment';
import BaseModalForm from 'components/BaseModalForm';
import { DEFAULTS as UNSUPPRESS_DEFAULTS } from "components/CustomDuration";
import FindingAPI from 'restAPI/FindingAPI';
import message from 'components/CustomMessage';
import Vocabulary from 'components/Vocabulary';
import { disablePastDates } from 'utils/dateUtils';

const FindingsReviewBulkAccept = forwardRef(({ actionTxt, afterClose, selectedRowKeys, setSelectedRowKeys = () => {}, selection }, ref) => {
	const [form] = Form.useForm();
	const dueDate = useRef();
	const isProposeDueDatePresent = useRef(false);

	const clearSelection = selection?.clear || (() => {});

	// Check if we're in filter-based "Select All" mode
	const isFilterMode = selection && !isEmpty(selection.filterMap);

	const [fields] = useState(() => {
		let isProposeAcceptRiskPresent = false;
		let isProposeControlledPresent = false;

		// When in filter mode (Select All), we can't analyze all findings' proposedData
		// since we only have visible rows. Show all possible fields to ensure nothing is missed.
		if (isFilterMode) {
			isProposeAcceptRiskPresent = true;
			isProposeControlledPresent = true;
			isProposeDueDatePresent.current = true;
			dueDate.current = moment().valueOf() / 1000;
		} else {
			// Individual selection mode: analyze actual proposedData
			_.forEach(selectedRowKeys, entry => {
				if (!entry.proposedData) return;
				_.forEach(entry.proposedData, proposeData => {
					switch (true) {
						case proposeData.proposedValue === 'ACCEPTRISK':
							isProposeAcceptRiskPresent = true;
							break;
						case proposeData.action === 'PROPOSE_DUE_DATE':
							isProposeDueDatePresent.current = true;
							dueDate.current = proposeData.proposedValue;
							break;
						case proposeData.proposedValue === 'CONTROLLED':
							isProposeControlledPresent = true;
							break;
						default:
							break;
					}
				});
			});
		}

	const fetchReasons = () => {
		if(isProposeAcceptRiskPresent && isProposeControlledPresent){
		const {promise, cancel} = FindingAPI.getAcceptRiskReasons();
		const {promise:promise2, cancel:cancel2} = FindingAPI.getControlledReasons();
		const reasonsPromise = Promise.all([promise,promise2]).then(([acceptRisk,controlled])=>{
			return uniq([...acceptRisk, ...controlled])
		})
		return {
			promise: reasonsPromise,
			cancel: () => {
				cancel();
				cancel2();
			}
		}
		}
		if(isProposeAcceptRiskPresent){
			return FindingAPI.getAcceptRiskReasons();
		}
		if(isProposeControlledPresent){
			return FindingAPI.getControlledReasons();
		}
	}

		const fields = [];
		if ((isProposeAcceptRiskPresent || isProposeControlledPresent) && actionTxt === 'Accept') {
			fields.push({
                name: 'reason',
                label: 'Reason',
                type: FieldTypes.SELECT,
				required: true,
                props: {
                    autoFocus: true,
					api: fetchReasons
                }
            });
			fields.push({
				name: 'unsupressAfterDuration',
				label: "Re-Open",
				type: FieldTypes.CUSTOM_DURATION,
				required: true,
				props: {
					showNeverOption: !isProposeAcceptRiskPresent && isProposeControlledPresent
				}
			})
		}

		if (isProposeDueDatePresent.current && actionTxt === 'Accept') {
			fields.push( {
			  name: 'dueDate',
			  label: <><Vocabulary name="resolution" /> Due Date</>,
			  type: FieldTypes.DATE,
			  colSpan: 12,
			  required:true,
			  props: {
				disabledDate: disablePastDates,
			   }
			});
		  }

		fields.push({
			name: "notes",
			type: FieldTypes.TEXT_AREA,
			required: true,
			label: "Note",
			customLengthRule: {
				max: 4000,
				message: "Maximum length is 4000 characters" 
			},
		});
		return fields;
	});

    const dueDateValue = (isProposeDueDatePresent.current && actionTxt === 'Accept') ? moment(parseInt(dueDate.current) * 1000) : moment();

	const onOk = async () => {
		await form.validateFields();
		let data = form.getFieldsValue();
		const newDueDateValue = data?.dueDate;
		delete data?.dueDate;
		const unSeppress = data.unsupressAfterDuration?.neverOrAfter && data.unsupressAfterDuration.neverOrAfter !== 'Never';

		data = {
			...data,
			approved: actionTxt === 'Accept',
			...((unSeppress && actionTxt === 'Accept') ? {
				unsupressAfterDuration: moment().add(+(data.unsupressAfterDuration.number), _.lowerCase(data.unsupressAfterDuration.durationType)).valueOf(),
			} : {
				unsupressAfterDuration: undefined
			}),
		}
		if (isProposeDueDatePresent.current && actionTxt === 'Accept' && newDueDateValue !== dueDateValue) {
			data.dueDate = newDueDateValue
		}

		// Use selection.submit() if available (supports filter-based mode)
		// Otherwise fall back to legacy direct API call
		if (selection?.submit) {
			await selection.submit(data, (payload) => FindingAPI.bulk.proposeBulkAction(payload)).then(() => {
				message.success(`Processing ${actionTxt} proposal, please check after some time.`);
				setSelectedRowKeys([]);
				clearSelection();
			});
		} else {
			// Legacy mode: direct API call with findingIds
			data.findingIds = _.map(selectedRowKeys, "id");
			await FindingAPI.bulk.proposeBulkAction(data).promise.then(() => {
				message.success(`Processing ${actionTxt} proposal, please check after some time.`);
				setSelectedRowKeys([]);
				clearSelection();
			});
		}
	}

	// Generate title with count information
	const count = selection?.totalCount || selectedRowKeys.length;
	const title = `Are you sure you want to '${actionTxt}' ${count} proposal${count === 1 ? '' : 's'}?`;

	return (
		<BaseModalForm
			title={title}
			form={form}
			fields={fields}
			ref={ref}
			onOk={onOk}
			modalProps={{ okText: actionTxt }}
			afterClose={afterClose}
			initialValues={{
				unsupressAfterDuration: UNSUPPRESS_DEFAULTS,
				dueDate: dueDateValue,
			}}
		/>
	)
})

export default FindingsReviewBulkAccept;
